/**
 * D274 (trial): the 3D world, loaded only when it is asked for. This wrapper is all the main
 * bundle carries; three.js and the renderer come in their own chunk on first use. While
 * the chunk loads the map's backdrop shows; if it cannot load, or WebGL cannot start, the
 * flat map passed as `fallback` is shown instead.
 */
import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ThreeWorldMap as ThreeWorldMapType, ThreeWorldMapProps } from './ThreeWorldMap.tsx';

let chunk: Promise<typeof ThreeWorldMapType> | null = null;
const load = () => (chunk ??= import('./ThreeWorldMap.tsx').then((m) => m.ThreeWorldMap));

export function LazyThreeWorld(
  props: Omit<ThreeWorldMapProps, 'startedAt' | 'onFail'> & { fallback: ComponentChildren },
) {
  const { fallback, ...rest } = props;
  const startedAt = useRef(performance.now());
  const [Map, setMap] = useState<typeof ThreeWorldMapType | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    load().then(
      (m) => live && setMap(() => m),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, []);
  if (failed) return <>{fallback}</>;
  if (!Map)
    return (
      <div
        aria-busy="true"
        aria-label={rest.label ?? 'Your farm'}
        style={{ background: 'var(--map-backdrop)', ...rest.style }}
      />
    );
  return <Map {...rest} startedAt={startedAt.current} onFail={() => setFailed(true)} />;
}
